import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { Upload, LogOut, TrendingUp, TrendingDown, DollarSign, AlertCircle, FileSpreadsheet, Loader2, Users, Settings, RefreshCw, Key, Minus, Shield, Activity, ChevronDown } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import ExpenseComparison from "./ExpenseComparison";
import MFASetup from "./MFASetup";
import { useFiscalYear } from "@/contexts/FiscalYearContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = ({ user, onLogout }) => {
  // Fiscal Year Context
  const { fiscalYears, currentFiscalYear, selectedFiscalYear, changeFiscalYear, loading: fyLoading, initializeFiscalYears } = useFiscalYear();
  
  const [uploading, setUploading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("all");
  const [combinedData, setCombinedData] = useState(null);
  const [trendData, setTrendData] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Location breakdown MTD/YTD toggle
  const [locationBreakdownView, setLocationBreakdownView] = useState("mtd");
  const [mtdLocationData, setMtdLocationData] = useState(null);
  const [currentPeriod, setCurrentPeriod] = useState("");
  const [availablePeriods, setAvailablePeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState("");
  
  // YTD state
  const [ytdData, setYtdData] = useState(null);
  const [ytdYears, setYtdYears] = useState([]);
  const [selectedYtdYear, setSelectedYtdYear] = useState("");
  const [ytdLoading, setYtdLoading] = useState(false);
  
  // Upload period override state
  const [uploadMonth, setUploadMonth] = useState("");
  
  // Password change state
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [passwordChanging, setPasswordChanging] = useState(false);
  const [uploadYear, setUploadYear] = useState(new Date().getFullYear().toString());
  
  // MFA setup state
  const [showMfaSetup, setShowMfaSetup] = useState(false);
  const [mfaEnforced, setMfaEnforced] = useState(false); // Track if MFA setup is being enforced
  
  // Monthly trend state (single-location users)
  const [monthlyTrendData, setMonthlyTrendData] = useState(null);
  const [trendLoading, setTrendLoading] = useState(false);

  // Corporate overhead state
  const [corporateOverhead, setCorporateOverhead] = useState(null);
  const [corporateLoading, setCorporateLoading] = useState(false);

  // Sorting state for performance ranking table
  const [sortField, setSortField] = useState('net_margin');
  const [sortDirection, setSortDirection] = useState('desc');

  // Admin view switcher state
  const [adminViewMode, setAdminViewMode] = useState('multi'); // 'multi' or 'single'
  const [adminSelectedLocation, setAdminSelectedLocation] = useState('');

  // Fiscal year comparison state
  const [comparisonData, setComparisonData] = useState(null);
  const [comparisonLoading, setComparisonLoading] = useState(false);

  // Detect if user has access to only one location or if admin is in single-location mode
  const isActualSingleLocation = user && user.assigned_locations && user.assigned_locations.length === 1;
  const isAdminSingleMode = user && user.role === 'admin' && adminViewMode === 'single';
  const isSingleLocation = isActualSingleLocation || isAdminSingleMode;
  const userLocation = isActualSingleLocation ? user.assigned_locations[0] : 
                      (isAdminSingleMode ? adminSelectedLocation : null);

  useEffect(() => {
    // Initialize fiscal years first
    initializeFiscalYears();
    loadData();
    loadYtdYears();
    loadCorporateOverhead();
    loadMonthlyTrend();
    checkMfaStatus(); // Check if MFA setup should be enforced
  }, []);

  // Check MFA status and enforce setup if not enabled
  const checkMfaStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/auth/mfa/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // If MFA is not enabled, force user to set it up
      if (!response.data.mfa_enabled) {
        setMfaEnforced(true);
        setShowMfaSetup(true);
      }
    } catch (error) {
      console.error('Error checking MFA status:', error);
    }
  };

  // Reload data when fiscal year changes
  useEffect(() => {
    if (selectedFiscalYear) {
      loadData();
      loadYtdYears();
      loadCorporateOverhead();
      loadMonthlyTrend();
      loadFiscalYearComparison();
      loadYtdData(selectedFiscalYear); // Load YTD for selected fiscal year
      if (locations.length > 0) {
        loadTrends(selectedLocation);
      }
    }
  }, [selectedFiscalYear]);
  
  // Reload fiscal year comparison when admin view mode changes
  useEffect(() => {
    if (user?.role === 'admin' && selectedFiscalYear) {
      loadFiscalYearComparison();
    }
  }, [adminViewMode, adminSelectedLocation]);

  useEffect(() => {
    if (locations.length > 0) {
      loadTrends(selectedLocation);
    }
  }, [selectedLocation]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Add cache-busting parameter to force fresh data
      const cacheBreaker = new Date().getTime();
      
      // Build fiscal year parameter
      const fyParam = selectedFiscalYear ? `&fiscal_year=${selectedFiscalYear}` : '';
      
      const [locationsRes, combinedRes, periodsRes] = await Promise.all([
        axios.get(`${API}/locations?_=${cacheBreaker}`),
        axios.get(`${API}/analysis/combined?_=${cacheBreaker}${fyParam}`),
        axios.get(`${API}/periods?_=${cacheBreaker}`)
      ]);
      setLocations(locationsRes.data.locations);
      setCombinedData(combinedRes.data);
      
      // Get the most recent period for MTD view
      if (periodsRes.data && periodsRes.data.periods && periodsRes.data.periods.length > 0) {
        // console.log('Periods data:', periodsRes.data.periods);
        const sortedPeriods = periodsRes.data.periods.sort();
        setAvailablePeriods(sortedPeriods);
        
        // Use selected period if set, otherwise use most recent
        const periodToUse = selectedPeriod || sortedPeriods[sortedPeriods.length - 1];
        // console.log('Most recent period:', mostRecentPeriod);
        setCurrentPeriod(periodToUse);
        if (!selectedPeriod) {
          setSelectedPeriod(periodToUse);
        }
        
        // Load MTD data for the period
        await loadMTDLocationData(periodToUse);
      } else {
        // console.log('No periods data available');
      }
      
      /* Debug log removed for production
      console.log('Dashboard Debug - Combined Data:', {
        total_expenses: combinedRes.data.total_expenses,
        net_ordinary_sum: combinedRes.data.by_location?.filter(loc => loc.location_name.toLowerCase() !== 'corp')
          .reduce((sum, loc) => sum + (loc.net_ordinary_income || 0), 0),
        locations_count: combinedRes.data.by_location?.length
      });
      */
      await loadTrends("all");
      await loadCorporateOverhead();
    } catch (error) {
      if (error.response?.status === 404) {
        // No data available yet - expected for new users
        setCombinedData(null);
        setLocations([]);
        setMtdLocationData(null);
      } else if (error.response?.status !== 404) {
        // Only show error toast for non-404 errors
        toast.error("Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle period change
  const handlePeriodChange = async (newPeriod) => {
    setSelectedPeriod(newPeriod);
    setCurrentPeriod(newPeriod);
    await loadMTDLocationData(newPeriod);
  };

  // Format period for display (e.g., "2024-12" -> "December 2024")
  const formatPeriodDisplay = (period) => {
    if (!period) return "";
    const [year, month] = period.split("-");
    const monthNames = ["January", "February", "March", "April", "May", "June", 
                        "July", "August", "September", "October", "November", "December"];
    return `${monthNames[parseInt(month) - 1]} ${year}`;
  };

  // Get fiscal year for a period
  const getFiscalYearForPeriod = (period) => {
    if (!period) return "";
    const [year, month] = period.split("-").map(Number);
    if (month >= 11) {
      return `FY${year}-${year + 1}`;
    } else {
      return `FY${year - 1}-${year}`;
    }
  };
  
  const loadMTDLocationData = async (period) => {
    try {
      // console.log('Loading MTD data for period:', period);
      const cacheBreaker = new Date().getTime();
      const response = await axios.get(`${API}/pl/cross-location-comparison?period=${period}&_=${cacheBreaker}`);
      // console.log('MTD data loaded:', response.data);
      setMtdLocationData(response.data);
    } catch (error) {
      console.error("Error loading MTD location data:", error);
      console.error("Error details:", error.response?.data);
      setMtdLocationData(null);
    }
  };

  const loadTrends = async (location) => {
    try {
      const params = location !== "all" ? { location_name: location } : {};
      // Add fiscal year parameter
      if (selectedFiscalYear) {
        params.fiscal_year = selectedFiscalYear;
      }
      const response = await axios.get(`${API}/analysis/trends`, { params });
      setTrendData(response.data.trends);
    } catch (error) {
      console.error("Error loading trends:", error);
    }
  };

  // YTD Data Loading Functions
  const loadYtdYears = async () => {
    try {
      const response = await axios.get(`${API}/ytd/years`);
      setYtdYears(response.data.fiscal_years || []);
    } catch (error) {
      console.error("Error loading YTD fiscal years:", error);
    }
  };

  const loadYtdData = async (fiscal_year) => {
    if (!fiscal_year) return;
    
    setYtdLoading(true);
    try {
      const response = await axios.get(`${API}/ytd/summary/${fiscal_year}`);
      setYtdData(response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        setYtdData(null);
      } else {
        console.error("Error loading YTD data:", error);
        toast.error("Failed to load YTD data");
      }
    } finally {
      setYtdLoading(false);
    }
  };

  const loadCorporateOverhead = async () => {
    // Load for both single-location and multi-location users
    setCorporateLoading(true);
    try {
      // Get Corporate Overhead configs filtered by fiscal year
      const params = selectedFiscalYear ? { fiscal_year: selectedFiscalYear } : {};
      const response = await axios.get(`${API}/config/corporate-overhead`, { params });
      const configs = response.data; // Array of configs
      
      // Calculate total Corporate OH across all periods
      const totalAmount = configs.reduce((sum, config) => sum + config.total_amount, 0);
      const avgAllocation = configs.length > 0 
        ? configs.reduce((sum, config) => sum + config.allocation_per_location, 0)
        : 0;
      
      setCorporateOverhead({
        amount: totalAmount,
        allocation_per_location: avgAllocation,
        locations_count: configs.length > 0 ? configs[0].locations_count : 0,
        by_period: configs // Store all period configs for Performance Ranking
      });
      
      // console.log('Dashboard Debug - Corporate Overhead Configs:', configs);
    } catch (error) {
      if (error.response?.status === 404 || error.response?.status === 403) {
        setCorporateOverhead(null);
      } else {
        console.error("Error loading corporate overhead:", error);
      }
    } finally {
      setCorporateLoading(false);
    }
  };

  const loadFiscalYearComparison = async () => {
    if (!selectedFiscalYear || !currentFiscalYear) return;
    
    // Only load comparison if we have at least 2 fiscal years
    if (fiscalYears.length < 2) {
      setComparisonData(null);
      return;
    }
    
    setComparisonLoading(true);
    try {
      // Compare selected fiscal year with previous one (or current with previous)
      const currentIndex = fiscalYears.indexOf(selectedFiscalYear);
      const previousFY = currentIndex < fiscalYears.length - 1 ? fiscalYears[currentIndex + 1] : null;
      
      if (!previousFY) {
        setComparisonData(null);
        return;
      }
      
      // Get month-by-month comparison
      const params = {
        fiscal_year_1: previousFY,
        fiscal_year_2: selectedFiscalYear
      };
      
      // If admin is viewing as single location, pass that location to filter
      if (isAdminSingleMode && adminSelectedLocation) {
        params.location_filter = adminSelectedLocation;
      }
      
      const response = await axios.get(`${API}/analysis/fiscal-year-monthly-comparison`, { params });
      
      setComparisonData(response.data);
    } catch (error) {
      console.error("Error loading fiscal year comparison:", error);
      setComparisonData(null);
    } finally {
      setComparisonLoading(false);
    }
  };

  const loadMonthlyTrend = async () => {
    if (!isSingleLocation || !userLocation) return;
    
    setTrendLoading(true);
    try {
      const cacheBuster = new Date().getTime();
      const response = await axios.get(`${API}/analysis/location/${userLocation}/monthly-trend?_=${cacheBuster}`);
      setMonthlyTrendData(response.data);
    } catch (error) {
      console.error("Error loading monthly trend:", error);
    } finally {
      setTrendLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    // Validation
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      toast.error("Please fill in all password fields");
      return;
    }
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }
    
    if (passwordForm.newPassword.length < 8) {
      toast.error("New password must be at least 8 characters long");
      return;
    }
    
    setPasswordChanging(true);
    try {
      await axios.put(`${API}/auth/change-password`, {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      });
      
      toast.success("Password changed successfully");
      setShowPasswordDialog(false);
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (error) {
      console.error("Error changing password:", error);
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setPasswordChanging(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    
    // Add period override if both month and year are selected
    if (uploadMonth && uploadYear) {
      const period = `${uploadYear}-${uploadMonth.padStart(2, '0')}`;
      formData.append("period", period);
    }

    setUploading(true);
    try {
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success(response.data.message);
      await loadData();
      await loadYtdYears(); // Reload YTD years in case new year was added
      await loadMonthlyTrend(); // Refresh 3-month trend for single location
      if (selectedFiscalYear) {
        await loadYtdData(selectedFiscalYear); // Refresh YTD data
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
      // Reset period selectors after upload
      setUploadMonth("");
      setUploadYear(new Date().getFullYear().toString()); // Reset to current year
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Sorting function for performance ranking table
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc'); // Default to descending for new fields
    }
  };

  // Get sort indicator icon
  const getSortIcon = (field) => {
    if (sortField !== field) return '↕️';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  // Generate month options (01-12)
  const monthOptions = [
    { value: "1", label: "January" },
    { value: "2", label: "February" },
    { value: "3", label: "March" },
    { value: "4", label: "April" },
    { value: "5", label: "May" },
    { value: "6", label: "June" },
    { value: "7", label: "July" },
    { value: "8", label: "August" },
    { value: "9", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" }
  ];

  // Generate year options (2020-2030 for historical support)
  const currentYear = new Date().getFullYear();
  const yearOptions = [];
  for (let year = 2020; year <= 2030; year++) {
    yearOptions.push({ value: year.toString(), label: year.toString() });
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="w-full mx-auto px-3 sm:px-4 lg:px-6 py-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2 sm:gap-3">
              <img 
                src="/rxDataMiningLogo.png" 
                alt="RxDataMining Logo" 
                className="h-10 w-auto sm:h-12 lg:h-14 flex-shrink-0"
              />
              <div>
                <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-slate-800">Financial Analysis Site</h1>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
              {/* Fiscal Year Selector */}
              <div className="flex flex-col gap-1">
                <label className="text-xs text-slate-600 font-medium">Fiscal Year</label>
                <Select 
                  value={selectedFiscalYear} 
                  onValueChange={changeFiscalYear}
                  disabled={fyLoading || fiscalYears.length === 0}
                >
                  <SelectTrigger className="w-32 sm:w-36 h-9 text-sm">
                    <SelectValue placeholder="Select FY" />
                  </SelectTrigger>
                  <SelectContent>
                    {fiscalYears.map(fy => (
                      <SelectItem key={fy} value={fy}>
                        {fy} {fy === currentFiscalYear && "(Current)"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Period/Month Selector */}
              <div className="flex flex-col gap-1">
                <label className="text-xs text-slate-600 font-medium">Viewing Month</label>
                <Select 
                  value={selectedPeriod} 
                  onValueChange={handlePeriodChange}
                  disabled={loading || availablePeriods.length === 0}
                >
                  <SelectTrigger className="w-36 sm:w-44 h-9 text-sm">
                    <SelectValue placeholder="Select Month">
                      {selectedPeriod && formatPeriodDisplay(selectedPeriod)}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {availablePeriods
                      .filter(period => {
                        // Filter periods to show only those in the selected fiscal year
                        if (!selectedFiscalYear) return true;
                        return getFiscalYearForPeriod(period) === selectedFiscalYear;
                      })
                      .sort()
                      .reverse()
                      .map(period => (
                        <SelectItem key={period} value={period}>
                          {formatPeriodDisplay(period)} 
                          <span className="text-xs text-slate-400 ml-2">({period})</span>
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Current Period Display Badge */}
              {currentPeriod && (
                <div className="hidden md:flex flex-col items-start px-3 py-1 bg-blue-50 border border-blue-200 rounded-lg">
                  <span className="text-xs text-blue-600 font-medium">MTD Data For:</span>
                  <span className="text-sm font-semibold text-blue-800">{formatPeriodDisplay(currentPeriod)}</span>
                  <span className="text-xs text-blue-500">{getFiscalYearForPeriod(currentPeriod)}</span>
                </div>
              )}
              
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-slate-800">{user?.full_name}</p>
                <p className="text-xs text-slate-500">{user?.email}</p>
                {user?.role === "admin" && (
                  <p className="text-xs text-emerald-600 font-medium">Administrator</p>
                )}
              </div>
              
              {/* Admin View Switcher - Only for Admin users */}
              {user?.role === "admin" && (
                <div className="flex flex-col gap-2 border-l border-slate-200 pl-4">
                  <Select value={adminViewMode} onValueChange={setAdminViewMode}>
                    <SelectTrigger className="w-28 sm:w-32 h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="multi">All Locations</SelectItem>
                      <SelectItem value="single">Single Store</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  {adminViewMode === 'single' && combinedData && (
                    <Select value={adminSelectedLocation} onValueChange={setAdminSelectedLocation}>
                      <SelectTrigger className="w-28 sm:w-32 h-8 text-xs">
                        <SelectValue placeholder="Select Store" />
                      </SelectTrigger>
                      <SelectContent>
                        {combinedData.by_location
                          ?.filter(loc => loc.location_name.toLowerCase() !== 'corp')
                          .map(location => (
                            <SelectItem key={location.location_name} value={location.location_name}>
                              {location.location_name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}
              
              {/* Manual Refresh Buttons */}
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    // Force complete data reload by clearing state first
                    setCombinedData(null);
                    setCorporateOverhead(null);
                    setLocations([]);
                    // Then reload data
                    setTimeout(() => {
                      loadData();
                      toast.success("Data refreshed");
                    }, 100);
                  }}
                  disabled={loading}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md border border-slate-300 disabled:opacity-50 transition-colors"
                  title="Refresh dashboard data"
                >
                  <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                  {loading ? 'Refreshing...' : 'Refresh'}
                </button>
                
                <button
                  onClick={() => {
                    toast.info("Hard refresh - reloading page...");
                    setTimeout(() => window.location.reload(), 500);
                  }}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-md border border-blue-300 transition-colors"
                  title="Force page reload to bypass all caching"
                >
                  <RefreshCw className="h-4 w-4" />
                  Hard Refresh
                </button>
              </div>
              
              {user?.role === "admin" && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      data-testid="admin-menu-btn"
                      className="gap-2 border-slate-300 text-slate-700 hover:bg-slate-100"
                    >
                      <Settings className="h-4 w-4" />
                      Admin
                      <ChevronDown className="h-3 w-3 ml-0.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      data-testid="admin-panel-link"
                      onClick={() => window.location.href = "/admin"}
                    >
                      <Settings className="h-4 w-4 mr-2" />
                      Admin Panel
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      data-testid="security-link"
                      onClick={() => window.location.href = "/security"}
                    >
                      <Shield className="h-4 w-4 mr-2" />
                      Security
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      data-testid="diagnostics-link"
                      onClick={() => window.location.href = "/diagnostics"}
                    >
                      <Activity className="h-4 w-4 mr-2" />
                      Diagnostics
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              
              <Button
                variant="outline"
                onClick={() => setShowMfaSetup(true)}
                className="gap-2 border-slate-300 text-slate-700 hover:bg-slate-100"
              >
                <Shield className="h-4 w-4" />
                MFA Settings
              </Button>
              
              <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
                <DialogTrigger asChild>
                  <Button
                    variant="outline"
                    className="gap-2 border-slate-300 text-slate-700 hover:bg-slate-100"
                  >
                    <Key className="h-4 w-4" />
                    Change Password
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Change Password</DialogTitle>
                    <DialogDescription>
                      Update your account password. Make sure to use a strong, unique password.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="current-password">Current Password</Label>
                      <Input
                        id="current-password"
                        type="password"
                        value={passwordForm.currentPassword}
                        onChange={(e) => setPasswordForm({...passwordForm, currentPassword: e.target.value})}
                        placeholder="Enter current password"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="new-password">New Password</Label>
                      <Input
                        id="new-password"
                        type="password"
                        value={passwordForm.newPassword}
                        onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                        placeholder="Enter new password (min 8 characters)"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirm-password">Confirm New Password</Label>
                      <Input
                        id="confirm-password"
                        type="password"
                        value={passwordForm.confirmPassword}
                        onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                        placeholder="Confirm new password"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-3">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowPasswordDialog(false);
                        setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
                      }}
                      disabled={passwordChanging}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handlePasswordChange}
                      disabled={passwordChanging}
                    >
                      {passwordChanging ? "Changing..." : "Change Password"}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
              
              <Button
                variant="outline"
                onClick={onLogout}
                data-testid="logout-button"
                className="gap-2"
              >
                <LogOut className="h-4 w-4" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="w-full mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6 lg:py-8">
        {/* Upload Section - Only visible to admin */}
        {user?.role === "admin" && (
          <Card className="mb-8 border-slate-200 shadow-sm">
            <CardHeader className="bg-slate-50 border-b border-slate-200">
              <CardTitle className="flex items-center gap-2 text-slate-800">
                <FileSpreadsheet className="h-5 w-5" />
                Upload Financial Data
              </CardTitle>
              <CardDescription>Upload CSV or Excel files with columns: Date, Revenue, Expenses, Location (or QuickBooks P&L reports)</CardDescription>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              {/* Period Override Section */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-amber-800 mb-2">Period Override (Optional)</h4>
                    <p className="text-xs text-amber-700 mb-3">
                      Select month/year to override auto-detection from the file. Leave blank to use auto-detected period.
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <label className="block text-xs font-medium text-slate-700 mb-1">Month</label>
                        <Select value={uploadMonth || "auto"} onValueChange={(value) => setUploadMonth(value === "auto" ? "" : value)}>
                          <SelectTrigger className="w-full h-8 text-sm">
                            <SelectValue placeholder="Auto-detect" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="auto">Auto-detect from file</SelectItem>
                            {monthOptions.map((month) => (
                              <SelectItem key={month.value} value={month.value}>
                                {month.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex-1">
                        <label className="block text-xs font-medium text-slate-700 mb-1">Year</label>
                        <Select value={uploadYear || "auto"} onValueChange={(value) => setUploadYear(value === "auto" ? "" : value)}>
                          <SelectTrigger className="w-full h-8 text-sm">
                            <SelectValue placeholder="Auto-detect" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="auto">Auto-detect from file</SelectItem>
                            {yearOptions.map((year) => (
                              <SelectItem key={year.value} value={year.value}>
                                {year.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    {uploadMonth && uploadYear && (
                      <p className="text-xs text-amber-700 mt-2">
                        📅 Data will be uploaded as: <strong>{monthOptions.find(m => m.value === uploadMonth)?.label} {uploadYear}</strong>
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* File Upload Section */}
              <div className="flex items-center gap-4">
                <label htmlFor="file-upload" className="cursor-pointer">
                  <Button
                    type="button"
                    data-testid="upload-file-button"
                    disabled={uploading}
                    className="bg-slate-800 hover:bg-slate-900"
                    onClick={() => document.getElementById('file-upload').click()}
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="mr-2 h-4 w-4" />
                        Choose File
                      </>
                    )}
                  </Button>
                </label>
                <input
                  id="file-upload"
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileUpload}
                  className="hidden"
                  data-testid="file-input"
                />
                <span className="text-sm text-slate-500">Supported: CSV, Excel (.xlsx, .xls), QuickBooks P&L</span>
              </div>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-slate-600" />
          </div>
        ) : !combinedData ? (
          <Card className="border-slate-200">
            <CardContent className="py-16 text-center">
              <AlertCircle className="h-12 w-12 text-slate-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-800 mb-2">No Data Available</h3>
              <p className="text-slate-600">Upload your first financial data file to get started</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Key Metrics */}
            <div className={`grid grid-cols-1 gap-6 mb-8 ${!isSingleLocation ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-3'}`}>
              <Card className="border-slate-200 shadow-sm" data-testid="total-revenue-card">
                <CardHeader className="pb-3">
                  <CardDescription className="text-xs font-medium text-slate-500 uppercase tracking-wide">Total Revenue (YTD)</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-baseline gap-2">
                    <DollarSign className="h-6 w-6 text-emerald-600" />
                    <span className="text-3xl font-bold text-slate-800">{formatCurrency(combinedData.total_revenue)}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">{combinedData.locations_count} locations</p>
                </CardContent>
              </Card>

              <Card className="border-slate-200 shadow-sm" data-testid="total-expenses-card">
                <CardHeader className="pb-3">
                  <CardDescription className="text-xs font-medium text-slate-500 uppercase tracking-wide">Total COGS + Expenses (YTD)</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-baseline gap-2">
                    <DollarSign className="h-6 w-6 text-rose-600" />
                    <span className="text-3xl font-bold text-slate-800">
                      {formatCurrency((combinedData.total_cogs || 0) + (combinedData.total_expenses || 0))}
                    </span>
                  </div>
                  <div className="mt-3 space-y-1 text-xs text-slate-600">
                    <div className="flex justify-between">
                      <span>RX Cost of Goods:</span>
                      <span className="font-semibold">{formatCurrency(combinedData.total_rx_cogs || 0)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>OTC Cost of Goods:</span>
                      <span className="font-semibold">{formatCurrency(combinedData.total_otc_cogs || 0)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Total Expenses:</span>
                      <span className="font-semibold">{formatCurrency(combinedData.total_expenses || 0)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-slate-200 shadow-sm" data-testid="net-profit-card">
                <CardHeader className="pb-3">
                  <CardDescription className="text-xs font-medium text-slate-500 uppercase tracking-wide">Net Profit/Loss before Corporate Expenses (YTD)</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-baseline gap-2">
                    {(() => {
                      // Calculate: Revenue - (COGS + Expenses)
                      const netProfitBeforeCorporate = (combinedData.total_revenue || 0) - ((combinedData.total_cogs || 0) + (combinedData.total_expenses || 0));
                      
                      return (
                        <>
                          <TrendingUp className={`h-6 w-6 ${netProfitBeforeCorporate >= 0 ? 'text-emerald-600' : 'text-rose-600'}`} />
                          <span className={`text-3xl font-bold ${netProfitBeforeCorporate >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                            {formatCurrency(netProfitBeforeCorporate)}
                          </span>
                        </>
                      );
                    })()}
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    Margin: {(() => {
                      const totalRevenue = combinedData.by_location
                        ?.filter(loc => loc.location_name.toLowerCase() !== 'corp')
                        .reduce((sum, loc) => sum + (loc.total_revenue || 0), 0) || 0;
                      const totalCOGS = combinedData.by_location
                        ?.filter(loc => loc.location_name.toLowerCase() !== 'corp')
                        .reduce((sum, loc) => sum + (loc.total_cogs || 0), 0) || 0;
                      const totalOperatingExpenses = combinedData.by_location
                        ?.filter(loc => loc.location_name.toLowerCase() !== 'corp')
                        .reduce((sum, loc) => sum + (loc.financial_records_expenses || loc.operating_expenses || 0), 0) || 0;
                      // For multi-location users: use user's specified Net Ordinary Income before Corporate Overhead value
                      // For single-location users: calculate using financial_records expenses
                      const netProfitBeforeCorporate = isSingleLocation 
                        ? (() => {
                            const userLocationData = combinedData.by_location.find(loc => loc.location_name === userLocation);
                            if (userLocationData) {
                              const revenue = userLocationData.total_revenue || 0;
                              const cogs = userLocationData.total_cogs || 0;
                              const operatingExpenses = userLocationData.financial_records_expenses || userLocationData.operating_expenses || 0;
                              return revenue - cogs - operatingExpenses;
                            }
                            return totalRevenue - totalCOGS - totalOperatingExpenses;
                          })()
                        : combinedData?.by_location
                            ?.filter(loc => loc.location_name.toLowerCase() !== 'corp')
                            .reduce((sum, loc) => sum + (loc.net_ordinary_income || 0), 0) || 0;
                      const revenueForMargin = isSingleLocation 
                        ? (combinedData.by_location.find(loc => loc.location_name === userLocation)?.total_revenue || totalRevenue)
                        : totalRevenue;
                      return ((netProfitBeforeCorporate / revenueForMargin) * 100).toFixed(1);
                    })()}%
                  </p>
                </CardContent>
              </Card>

              {/* Net Income after Corporate Expenses - Only for multi-location users */}
              {!isSingleLocation && (
                <Card className="border-slate-200 shadow-sm" data-testid="net-income-after-corporate-card">
                  <CardHeader className="pb-3">
                    <CardDescription className="text-xs font-medium text-slate-500 uppercase tracking-wide">Net Income after Corporate Expenses (YTD)</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {corporateLoading ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="h-6 w-6 animate-spin text-slate-600" />
                      </div>
                    ) : corporateOverhead ? (
                      <>
                        <div className="flex items-baseline gap-2">
                          {(() => {
                            // Calculate: Revenue - (COGS + Expenses) - Corporate Overhead
                            const netProfitBeforeCorporate = (combinedData.total_revenue || 0) - ((combinedData.total_cogs || 0) + (combinedData.total_expenses || 0));
                            const corporateOverheadAmount = corporateOverhead?.amount || 0;
                            const netIncomeAfterCorporate = netProfitBeforeCorporate - corporateOverheadAmount;
                            
                            return (
                              <>
                                <TrendingUp className={`h-6 w-6 ${netIncomeAfterCorporate >= 0 ? 'text-emerald-600' : 'text-rose-600'}`} />
                                <span className={`text-3xl font-bold ${netIncomeAfterCorporate >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                  {formatCurrency(netIncomeAfterCorporate)}
                                </span>
                              </>
                            );
                          })()}
                        </div>
                        <p className="text-xs text-slate-500 mt-2">
                          Net Other Income: {formatCurrency(corporateOverhead?.amount || 0)}
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                          Period: {corporateOverhead.period}
                        </p>
                      </>
                    ) : (
                      <div className="flex items-center justify-center py-4">
                        <AlertCircle className="h-6 w-6 text-slate-400" />
                        <span className="text-sm text-slate-500 ml-2">No corporate data</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>


            {/* Analysis Tabs */}
            <Tabs defaultValue="trends" className="space-y-6">
              <TabsList className="bg-gradient-to-r from-slate-100 to-slate-50 border-2 border-slate-300 p-2 rounded-lg shadow-md relative z-10 mb-4">
                <TabsTrigger 
                  value="trends" 
                  data-testid="trends-tab"
                  className="data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-lg hover:bg-blue-100 transition-all duration-200 px-4 py-2 font-medium rounded-md"
                >
                  📊 Performance Insights
                </TabsTrigger>
                {!isSingleLocation && (
                  <TabsTrigger 
                    value="locations" 
                    data-testid="locations-tab"
                    className="data-[state=active]:bg-green-600 data-[state=active]:text-white data-[state=active]:shadow-lg hover:bg-green-100 transition-all duration-200 px-4 py-2 font-medium rounded-md"
                  >
                    🏪 Location Breakdown
                  </TabsTrigger>
                )}
                <TabsTrigger 
                  value="comparison" 
                  data-testid="comparison-tab"
                  className="data-[state=active]:bg-purple-600 data-[state=active]:text-white data-[state=active]:shadow-lg hover:bg-purple-100 transition-all duration-200 px-4 py-2 font-medium rounded-md"
                >
                  📈 Expense Comparison
                </TabsTrigger>
                {isSingleLocation && (
                  <TabsTrigger 
                    value="mtd" 
                    data-testid="mtd-tab"
                    className="data-[state=active]:bg-teal-600 data-[state=active]:text-white data-[state=active]:shadow-lg hover:bg-teal-100 transition-all duration-200 px-4 py-2 font-medium rounded-md"
                  >
                    📆 Month-to-Date
                  </TabsTrigger>
                )}
                <TabsTrigger 
                  value="ytd" 
                  data-testid="ytd-tab"
                  className="data-[state=active]:bg-orange-600 data-[state=active]:text-white data-[state=active]:shadow-lg hover:bg-orange-100 transition-all duration-200 px-4 py-2 font-medium rounded-md"
                >
                  📅 Year-to-Date
                </TabsTrigger>
                {fiscalYears.length >= 2 && (
                  <TabsTrigger 
                    value="fiscal-comparison" 
                    data-testid="fiscal-comparison-tab"
                    className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white data-[state=active]:shadow-lg hover:bg-indigo-100 transition-all duration-200 px-4 py-2 font-medium rounded-md"
                  >
                    📊 Fiscal Year Comparison
                  </TabsTrigger>
                )}
              </TabsList>

              <TabsContent value="trends" data-testid="trends-content">
                <div className="space-y-6">
                  {/* Performance Insights Header */}
                  <Card className="border-slate-200 shadow-sm bg-gradient-to-r from-blue-50 to-indigo-50">
                    <CardHeader>
                      <CardTitle className="text-xl text-slate-800 flex items-center gap-2">
                        <TrendingUp className="h-6 w-6 text-blue-600" />
                        Performance Insights & Actionable Data
                      </CardTitle>
                      <CardDescription>Identify top performers, improvement opportunities, and key focus areas (excludes corporate/administrative locations)</CardDescription>
                    </CardHeader>
                  </Card>

                  {combinedData && combinedData.by_location ? (
                    <>
                      {/* Top Performers Section */}
                      <div className={`grid grid-cols-1 gap-6 ${isSingleLocation ? 'lg:grid-cols-3' : 'lg:grid-cols-2'}`}>
                        {/* Gross Profit Margin */}
                        <Card className={`border-emerald-200 ${(() => {
                          if (isSingleLocation) {
                            const userLocationData = combinedData.by_location.find(loc => loc.location_name === userLocation);
                            if (userLocationData) {
                              const gross_margin = userLocationData.total_revenue > 0 ? (userLocationData.gross_profit / userLocationData.total_revenue * 100) : 0;
                              return gross_margin >= 20 ? 'bg-emerald-50' : 'bg-red-50 border-red-200';
                            }
                          }
                          return 'bg-emerald-50';
                        })()}`}>
                          <CardHeader className={`${(() => {
                            if (isSingleLocation) {
                              const userLocationData = combinedData.by_location.find(loc => loc.location_name === userLocation);
                              if (userLocationData) {
                                const gross_margin = userLocationData.total_revenue > 0 ? (userLocationData.gross_profit / userLocationData.total_revenue * 100) : 0;
                                return gross_margin >= 20 ? 'bg-emerald-100 border-b border-emerald-200' : 'bg-red-100 border-b border-red-200';
                              }
                            }
                            return 'bg-emerald-100 border-b border-emerald-200';
                          })()} `}>
                            <CardTitle className={`${(() => {
                              if (isSingleLocation) {
                                const userLocationData = combinedData.by_location.find(loc => loc.location_name === userLocation);
                                if (userLocationData) {
                                  const gross_margin = userLocationData.total_revenue > 0 ? (userLocationData.gross_profit / userLocationData.total_revenue * 100) : 0;
                                  return gross_margin >= 20 ? 'text-emerald-800' : 'text-red-800';
                                }
                              }
                              return 'text-emerald-800';
                            })()} flex items-center gap-2`}>
                              <TrendingUp className="h-5 w-5" />
                              {isSingleLocation ? '📊 Gross Profit Margin' : '🏆 Best Gross Profit Margin'}
                            </CardTitle>
                            <div className="flex gap-2 mt-2">
                              <span className="px-2 py-0.5 bg-white/60 rounded text-xs font-medium text-slate-700">{selectedFiscalYear || 'All FY'}</span>
                              <span className="px-2 py-0.5 bg-white/60 rounded text-xs font-medium text-slate-700">YTD through {formatPeriodDisplay(currentPeriod)}</span>
                            </div>
                          </CardHeader>
                          <CardContent className="pt-4">
                            {(() => {
                              if (isSingleLocation) {
                                const userLocationData = combinedData.by_location.find(loc => loc.location_name === userLocation);
                                if (userLocationData) {
                                  const gross_margin = userLocationData.total_revenue > 0 ? (userLocationData.gross_profit / userLocationData.total_revenue * 100) : 0;
                                  const colorClass = gross_margin >= 20 ? 'text-emerald-600' : 'text-red-600';
                                  
                                  return (
                                    <div className="text-center">
                                      <h3 className={`text-2xl font-bold ${gross_margin >= 20 ? 'text-emerald-700' : 'text-red-700'}`}>{userLocationData.location_name}</h3>
                                      <p className={`text-3xl font-bold ${colorClass}`}>{gross_margin.toFixed(1)}%</p>
                                      <p className={`text-sm ${colorClass} mt-1`}>Gross Profit: {formatCurrency(userLocationData.gross_profit)}</p>
                                      <p className={`text-sm ${colorClass}`}>Revenue: {formatCurrency(userLocationData.total_revenue)}</p>
                                      {gross_margin < 20 && (
                                        <div className="mt-2 text-xs text-red-600 bg-red-100 rounded p-2">
                                          ⚠️ Below 20% target - Review COGS optimization
                                        </div>
                                      )}
                                    </div>
                                  );
                                }
                                return <div className="text-center text-slate-500">No data available</div>;
                              } else {
                                const bestGrossMargin = combinedData.by_location
                                  .filter(loc => loc.location_name.toLowerCase() !== 'corp' && loc.location_name.toLowerCase() !== 'cmed') // Exclude corp and cmed from analysis
                                  .map(loc => ({
                                    ...loc,
                                    gross_margin: loc.total_revenue > 0 ? (loc.gross_profit / loc.total_revenue * 100) : 0
                                  }))
                                  .sort((a, b) => b.gross_margin - a.gross_margin)[0];
                                
                                return (
                                  <div className="text-center">
                                    <h3 className="text-2xl font-bold text-emerald-700">{bestGrossMargin.location_name}</h3>
                                    <p className="text-3xl font-bold text-emerald-600">{bestGrossMargin.gross_margin.toFixed(1)}%</p>
                                    <p className="text-sm text-emerald-600 mt-1">Gross Profit: {formatCurrency(bestGrossMargin.gross_profit)}</p>
                                    <p className="text-sm text-emerald-600">Revenue: {formatCurrency(bestGrossMargin.total_revenue)}</p>
                                    <p className="text-xs text-slate-500 mt-3 italic">Custom Meds is excluded from this calculation.</p>
                                  </div>
                                );
                              }
                            })()}
                          </CardContent>
                        </Card>

                        {/* Net Profit Margin / Net Profit Before Corporate Overhead */}
                        <Card className={`border-blue-200 ${(() => {
                          if (isSingleLocation) {
                            const userLocationData = combinedData.by_location.find(loc => loc.location_name === userLocation);
                            if (userLocationData) {
                              const netBeforeCorporate = (userLocationData.total_revenue || 0) - (userLocationData.total_cogs || 0) - (userLocationData.operating_expenses || 0);
                              return netBeforeCorporate >= 0 ? 'bg-blue-50' : 'bg-red-50 border-red-200';
                            }
                          }
                          return 'bg-blue-50';
                        })()}`}>
                          <CardHeader className={`${(() => {
                            if (isSingleLocation) {
                              const userLocationData = combinedData.by_location.find(loc => loc.location_name === userLocation);
                              if (userLocationData) {
                                const netBeforeCorporate = (userLocationData.total_revenue || 0) - (userLocationData.total_cogs || 0) - (userLocationData.operating_expenses || 0);
                                return netBeforeCorporate >= 0 ? 'bg-blue-100 border-b border-blue-200' : 'bg-red-100 border-b border-red-200';
                              }
                            }
                            return 'bg-blue-100 border-b border-blue-200';
                          })()} `}>
                            <CardTitle className={`${(() => {
                              if (isSingleLocation) {
                                const userLocationData = combinedData.by_location.find(loc => loc.location_name === userLocation);
                                if (userLocationData) {
                                  const netBeforeCorporate = (userLocationData.total_revenue || 0) - (userLocationData.total_cogs || 0) - (userLocationData.operating_expenses || 0);
                                  return netBeforeCorporate >= 0 ? 'text-blue-800' : 'text-red-800';
                                }
                              }
                              return 'text-blue-800';
                            })()} flex items-center gap-2`}>
                              <DollarSign className="h-5 w-5" />
                              {isSingleLocation ? '💰 Net Profit Before Corporate Overhead' : '🎯 Best Net Profit Margin'}
                            </CardTitle>
                            <div className="flex gap-2 mt-2">
                              <span className="px-2 py-0.5 bg-white/60 rounded text-xs font-medium text-slate-700">{selectedFiscalYear || 'All FY'}</span>
                              <span className="px-2 py-0.5 bg-white/60 rounded text-xs font-medium text-slate-700">YTD through {formatPeriodDisplay(currentPeriod)}</span>
                            </div>
                          </CardHeader>
                          <CardContent className="pt-4">
                            {(() => {
                              if (isSingleLocation) {
                                const userLocationData = combinedData.by_location.find(loc => loc.location_name === userLocation);
                                if (userLocationData) {
                                  // For single location: Show Net Profit Before Corporate Overhead
                                  const netBeforeCorporate = (userLocationData.total_revenue || 0) - (userLocationData.total_cogs || 0) - (userLocationData.operating_expenses || 0);
                                  const colorClass = netBeforeCorporate >= 0 ? 'text-blue-600' : 'text-red-600';
                                  
                                  return (
                                    <div className="text-center">
                                      <h3 className={`text-2xl font-bold ${netBeforeCorporate >= 0 ? 'text-blue-700' : 'text-red-700'}`}>{userLocationData.location_name}</h3>
                                      <p className={`text-3xl font-bold ${colorClass}`}>{formatCurrency(netBeforeCorporate)}</p>
                                      <p className={`text-sm ${colorClass} mt-2`}>
                                        Revenue - COGS - Operating Expenses
                                      </p>
                                      {netBeforeCorporate < 0 && (
                                        <div className="mt-2 text-xs text-red-600 bg-red-100 rounded p-2">
                                          ⚠️ Negative profit before corporate overhead
                                        </div>
                                      )}
                                    </div>
                                  );
                                }
                                return <div className="text-center text-slate-500">No data available</div>;
                              } else {
                                const bestNetMargin = combinedData.by_location
                                  .filter(loc => loc.location_name.toLowerCase() !== 'corp' && loc.location_name.toLowerCase() !== 'cmed') // Exclude corp and cmed from analysis
                                  .map(loc => ({
                                    ...loc,
                                    net_margin: (loc.total_profit_loss / loc.total_revenue * 100) // Uses correct total_profit_loss (Revenue - COGS - Expenses)
                                  }))
                                  .sort((a, b) => b.net_margin - a.net_margin)[0];
                                
                                return (
                                  <div className="text-center">
                                    <h3 className="text-2xl font-bold text-blue-700">{bestNetMargin.location_name}</h3>
                                    <p className="text-3xl font-bold text-blue-600">{bestNetMargin.net_margin.toFixed(1)}%</p>
                                    <p className="text-sm text-blue-600 mt-2">Net Profit: {formatCurrency(bestNetMargin.total_profit_loss)}</p>
                                    <p className="text-xs text-slate-500 mt-3 italic">Custom Meds is excluded from this calculation.</p>
                                  </div>
                                );
                              }
                            })()}
                          </CardContent>
                        </Card>

                        {/* Operating Expenses - Single Location Only */}
                        {isSingleLocation && (
                          <Card className={`${(() => {
                            const userLocationData = combinedData.by_location.find(loc => loc.location_name === userLocation);
                            if (userLocationData) {
                              const operatingExpenses = userLocationData.operating_expenses || userLocationData.total_expenses;
                              const expenseRatio = (operatingExpenses / userLocationData.total_revenue * 100);
                              return expenseRatio <= 19 ? 'border-blue-200 bg-blue-50' : 'border-red-200 bg-red-50';
                            }
                            return 'border-blue-200 bg-blue-50';
                          })()}`}>
                            <CardHeader className={`${(() => {
                              const userLocationData = combinedData.by_location.find(loc => loc.location_name === userLocation);
                              if (userLocationData) {
                                const operatingExpenses = userLocationData.operating_expenses || userLocationData.total_expenses;
                                const expenseRatio = (operatingExpenses / userLocationData.total_revenue * 100);
                                return expenseRatio <= 19 ? 'bg-blue-100 border-b border-blue-200' : 'bg-red-100 border-b border-red-200';
                              }
                              return 'bg-blue-100 border-b border-blue-200';
                            })()} `}>
                              <CardTitle className={`${(() => {
                                const userLocationData = combinedData.by_location.find(loc => loc.location_name === userLocation);
                                if (userLocationData) {
                                  const operatingExpenses = userLocationData.operating_expenses || userLocationData.total_expenses;
                                  const expenseRatio = (operatingExpenses / userLocationData.total_revenue * 100);
                                  return expenseRatio <= 19 ? 'text-blue-800' : 'text-red-800';
                                }
                                return 'text-blue-800';
                              })()} flex items-center gap-2`}>
                                <FileSpreadsheet className="h-5 w-5" />
                                💰 Operating Expenses
                              </CardTitle>
                            </CardHeader>
                            <CardContent className="pt-4">
                              {(() => {
                                const userLocationData = combinedData.by_location.find(loc => loc.location_name === userLocation);
                                if (userLocationData) {
                                  // For single-location users, use financial_records total expenses (which should be $82,714.25 for ridglea)
                                  // This represents true operating expenses as recorded in financial_records
                                  const operatingExpenses = userLocationData.financial_records_expenses || userLocationData.operating_expenses || userLocationData.total_expenses;
                                  const expenseRatio = (operatingExpenses / userLocationData.total_revenue * 100);
                                  const colorClass = expenseRatio <= 19 ? 'text-blue-600' : 'text-red-600';
                                  
                                  return (
                                    <div className="text-center">
                                      <h3 className={`text-2xl font-bold ${expenseRatio <= 19 ? 'text-blue-700' : 'text-red-700'}`}>{userLocationData.location_name}</h3>
                                      <p className={`text-3xl font-bold ${colorClass}`}>{formatCurrency(operatingExpenses)}</p>
                                      <p className={`text-sm ${colorClass} mt-2`}>
                                        Operating Expense Ratio: {expenseRatio.toFixed(1)}%
                                      </p>
                                      {expenseRatio > 19 && (
                                        <div className="mt-2 text-xs text-red-600 bg-red-100 rounded p-2">
                                          ⚠️ Above 19% target - Review expense optimization
                                        </div>
                                      )}
                                      {expenseRatio <= 19 && (
                                        <div className="mt-2 text-xs text-blue-600 bg-blue-100 rounded p-2">
                                          ✅ Within 19% target - Good expense control
                                        </div>
                                      )}
                                    </div>
                                  );
                                }
                                return <div className="text-center text-slate-500">No data available</div>;
                              })()}
                            </CardContent>
                          </Card>
                        )}
                      </div>

                      {/* Additional Cards for Single Location or Areas Needing Attention for Multi-Location */}
                      <div className={`grid grid-cols-1 gap-6 ${isSingleLocation ? 'lg:grid-cols-1' : 'lg:grid-cols-2'}`}>
                        {isSingleLocation ? (
                          // Single location: show Net Profit Before Corporate Expenses
                          <Card className={`${(() => {
                            const userLocationData = combinedData.by_location.find(loc => loc.location_name === userLocation);
                            if (userLocationData) {
                              const netProfit = userLocationData.total_profit_loss;
                              return netProfit >= 0 ? 'border-green-300 bg-green-100' : 'border-red-200 bg-red-50';
                            }
                            return 'border-green-300 bg-green-100';
                          })()}`}>
                            <CardHeader className={`${(() => {
                              const userLocationData = combinedData.by_location.find(loc => loc.location_name === userLocation);
                              if (userLocationData) {
                                const netProfit = userLocationData.total_profit_loss;
                                return netProfit >= 0 ? 'bg-green-200 border-b border-green-300' : 'bg-red-100 border-b border-red-200';
                              }
                              return 'bg-green-200 border-b border-green-300';
                            })()}`}>
                              <CardTitle className={`${(() => {
                                const userLocationData = combinedData.by_location.find(loc => loc.location_name === userLocation);
                                if (userLocationData) {
                                  // Use same calculation as Performance Ranking table
                                  const netOrdinaryIncome = (userLocationData.total_revenue || 0) - ((userLocationData.total_cogs || 0) + (userLocationData.financial_records_expenses || 0));
                                  const corpOHInterestExp = userLocationData.corp_oh_interest_exp || 0;
                                  const netProfit = netOrdinaryIncome - corpOHInterestExp;
                                  return netProfit >= 0 ? 'text-green-800' : 'text-red-800';
                                }
                                return 'text-green-800';
                              })()} flex items-center gap-2`}
                              title="Net Profit is approximate within ±5% due to Corporate Overhead allocation across all locations">
                                <DollarSign className="h-5 w-5" />
                                💼 Net Profit After Corporate Expenses
                              </CardTitle>
                            </CardHeader>
                            <CardContent className="pt-4">
                              {(() => {
                                const userLocationData = combinedData.by_location.find(loc => loc.location_name === userLocation);
                                if (userLocationData) {
                                  // Calculate Net Ordinary Income: Revenue - COGS - Operating Expenses
                                  const netOrdinaryIncome = (userLocationData.total_revenue || 0) - (userLocationData.total_cogs || 0) - (userLocationData.operating_expenses || 0);
                                  
                                  // Get Corporate Overhead allocation for this location
                                  const corpOHPerLocation = corporateOverhead?.allocation_per_location || 0;
                                  
                                  // Net Profit After Corporate = Net Ordinary Income - Corporate Overhead
                                  const netProfit = netOrdinaryIncome - corpOHPerLocation;
                                  const colorClass = netProfit >= 0 ? 'text-green-700' : 'text-red-600';
                                  
                                  return (
                                    <div className="text-center">
                                      <h3 className={`text-2xl font-bold ${netProfit >= 0 ? 'text-green-800' : 'text-red-700'}`}>{userLocationData.location_name}</h3>
                                      <p className={`text-3xl font-bold ${colorClass}`}>{formatCurrency(netProfit)}</p>
                                      <p className={`text-sm ${colorClass} mt-2`}>
                                        Operational Profit: {netProfit >= 0 ? 'Positive' : 'Loss'}
                                      </p>
                                      <div className={`mt-2 text-xs rounded p-2 ${netProfit >= 0 ? 'text-green-700 bg-green-200' : 'text-red-600 bg-red-100'}`}>
                                        After corporate overhead allocation (${corpOHPerLocation.toLocaleString()})
                                      </div>
                                    </div>
                                  );
                                }
                                return <div className="text-center text-slate-500">No data available</div>;
                              })()}
                            </CardContent>
                          </Card>
                        ) : (
                          // Multi-location: show Areas Needing Attention
                          <>
                            {/* Worst Performing Store */}
                            <Card className="border-red-200 bg-red-50">
                              <CardHeader className="bg-red-100 border-b border-red-200">
                                <CardTitle className="text-red-800 flex items-center gap-2">
                                  <AlertCircle className="h-5 w-5" />
                                  ⚠️ Needs Immediate Attention
                                </CardTitle>
                                <div className="flex gap-2 mt-2">
                                  <span className="px-2 py-0.5 bg-white/60 rounded text-xs font-medium text-slate-700">{selectedFiscalYear || 'All FY'}</span>
                                  <span className="px-2 py-0.5 bg-white/60 rounded text-xs font-medium text-slate-700">YTD through {formatPeriodDisplay(currentPeriod)}</span>
                                </div>
                              </CardHeader>
                              <CardContent className="pt-4">
                                {(() => {
                                  // Calculate Net Profit same way as Performance Ranking table for consistency
                                  const locationsWithCalculatedProfit = combinedData.by_location
                                    .filter(loc => loc.location_name.toLowerCase() !== 'corp' && loc.location_name.toLowerCase() !== 'cmed' && loc.location_name.toLowerCase() !== 'hometown')
                                    .map(loc => {
                                      const netOrdinary = (loc.total_revenue || 0) - (loc.total_cogs || 0) - (loc.operating_expenses || 0);
                                      const corpOHPerLocation = corporateOverhead?.allocation_per_location || 0;
                                      const calculatedNetProfit = netOrdinary - corpOHPerLocation;
                                      return {
                                        ...loc,
                                        calculated_net_profit: calculatedNetProfit,
                                        net_margin: loc.total_revenue > 0 ? (calculatedNetProfit / loc.total_revenue * 100) : 0
                                      };
                                    });
                                  
                                  // Sort by net margin (worst margin percentage = needs most attention)
                                  const sorted = locationsWithCalculatedProfit.sort((a, b) => a.net_margin - b.net_margin);
                                  const worstPerformer = sorted[0];
                                  
                                  return (
                                    <div className="text-center">
                                      <h3 className="text-2xl font-bold text-red-700">{worstPerformer.location_name}</h3>
                                      <p className="text-2xl font-bold text-red-600">{formatCurrency(worstPerformer.calculated_net_profit)}</p>
                                      <p className="text-sm text-red-600 mt-2">
                                        Loss Margin: {worstPerformer.net_margin.toFixed(1)}%
                                      </p>
                                      <div className="mt-3 text-xs text-red-600 bg-red-100 rounded p-2">
                                        Action: Review expenses & optimize operations
                                      </div>
                                      <p className="text-xs text-slate-500 mt-3 italic">Ranked by loss margin %. Excludes Corporate, Custom Meds, and Hometown.</p>
                                    </div>
                                  );
                                })()}
                              </CardContent>
                            </Card>

                            {/* Highest Operating Expenses */}
                            <Card className="border-amber-200 bg-amber-50">
                              <CardHeader className="bg-amber-100 border-b border-amber-200">
                                <CardTitle className="text-amber-800 flex items-center gap-2">
                                  <AlertCircle className="h-5 w-5 text-amber-500" />
                                  💰 Highest Operating Expenses
                                </CardTitle>
                                <div className="flex gap-2 mt-2">
                                  <span className="px-2 py-0.5 bg-white/60 rounded text-xs font-medium text-slate-700">{selectedFiscalYear || 'All FY'}</span>
                                  <span className="px-2 py-0.5 bg-white/60 rounded text-xs font-medium text-slate-700">YTD through {formatPeriodDisplay(currentPeriod)}</span>
                                </div>
                              </CardHeader>
                              <CardContent className="pt-4">
                                {(() => {
                                  const highestExpenseRatio = combinedData.by_location
                                    .filter(loc => loc.location_name.toLowerCase() !== 'corp' && loc.location_name.toLowerCase() !== 'cmed' && loc.location_name.toLowerCase() !== 'hometown')
                                    .sort((a, b) => (b.operating_expenses / b.total_revenue) - (a.operating_expenses / a.total_revenue))[0];
                                  const expenseRatio = (highestExpenseRatio.operating_expenses / highestExpenseRatio.total_revenue * 100);
                                  const colorClass = expenseRatio <= 19 ? 'text-blue-600' : 'text-amber-600';
                                  
                                  return (
                                    <div className="text-center">
                                      <h3 className={`text-2xl font-bold ${expenseRatio <= 19 ? 'text-blue-700' : 'text-amber-700'}`}>{highestExpenseRatio.location_name}</h3>
                                      <p className={`text-3xl font-bold ${colorClass}`}>{expenseRatio.toFixed(1)}%</p>
                                      <p className={`text-sm ${colorClass} mt-2`}>
                                        Operating Expenses: {formatCurrency(highestExpenseRatio.operating_expenses)}
                                      </p>
                                      {expenseRatio > 19 && (
                                        <div className="mt-2 text-xs text-amber-600 bg-amber-100 rounded p-2">
                                          Action: Audit operating expense categories for optimization
                                        </div>
                                      )}
                                      <p className="text-xs text-slate-500 mt-3 italic">Excludes Corporate, Custom Meds, and Hometown.</p>
                                    </div>
                                  );
                                })()}
                              </CardContent>
                            </Card>
                          </>
                        )}
                      </div>

                      {/* 3-Month Trend Card - Single Location Only */}
                      {isSingleLocation && monthlyTrendData && (
                        <div className="mt-6">
                          <Card className="border-purple-200 bg-purple-50">
                            <CardHeader className="bg-purple-100 border-b border-purple-200">
                              <CardTitle className="text-purple-800 flex items-center gap-2">
                                <TrendingUp className="h-5 w-5" />
                                📈 3-Month Net Profit Trend
                              </CardTitle>
                              <CardDescription className="text-purple-700">
                                Tracking performance over the last 3 months
                              </CardDescription>
                            </CardHeader>
                            <CardContent className="pt-6">
                              {trendLoading ? (
                                <div className="flex items-center justify-center py-8">
                                  <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
                                </div>
                              ) : (
                                <div className="space-y-6">
                                  <ResponsiveContainer width="100%" height={300}>
                                    <LineChart
                                      data={monthlyTrendData.months}
                                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                                    >
                                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                                      <XAxis 
                                        dataKey="period" 
                                        stroke="#666"
                                        tick={{ fill: '#666' }}
                                      />
                                      <YAxis 
                                        stroke="#666"
                                        tick={{ fill: '#666' }}
                                        tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                                      />
                                      <Tooltip 
                                        formatter={(value) => `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                                        contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc' }}
                                      />
                                      <Legend />
                                      <Line 
                                        type="monotone" 
                                        dataKey="net_before_corporate" 
                                        stroke="#10b981" 
                                        strokeWidth={2}
                                        name="Net Before Corporate OH"
                                        dot={{ fill: '#10b981', r: 5 }}
                                      />
                                      <Line 
                                        type="monotone" 
                                        dataKey="net_after_corporate" 
                                        stroke="#8b5cf6" 
                                        strokeWidth={2}
                                        name="Net After Corporate OH"
                                        dot={{ fill: '#8b5cf6', r: 5 }}
                                      />
                                    </LineChart>
                                  </ResponsiveContainer>
                                  
                                  {/* Summary Table */}
                                  <div className="mt-4">
                                    <table className="w-full text-sm">
                                      <thead>
                                        <tr className="border-b border-purple-200">
                                          <th className="text-left py-2 text-purple-800">Month</th>
                                          <th className="text-right py-2 text-purple-800">Revenue</th>
                                          <th className="text-right py-2 text-green-700">Before OH</th>
                                          <th className="text-right py-2 text-red-700">Corp OH</th>
                                          <th className="text-right py-2 text-purple-800 font-bold">After OH</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {monthlyTrendData.months.map((month, idx) => (
                                          <tr key={idx} className="border-b border-purple-100">
                                            <td className="py-2 font-medium">{month.period}</td>
                                            <td className="text-right">{formatCurrency(month.revenue)}</td>
                                            <td className="text-right text-green-600 font-medium">
                                              {formatCurrency(month.net_before_corporate)}
                                            </td>
                                            <td className="text-right text-red-600">
                                              ({formatCurrency(month.corporate_overhead)})
                                            </td>
                                            <td className="text-right text-purple-700 font-bold">
                                              {formatCurrency(month.net_after_corporate)}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        </div>
                      )}

                      {/* Performance Ranking Table - Only for multi-location users */}
                      {!isSingleLocation && (
                        <Card className="border-slate-200 shadow-sm">
                          <CardHeader className="bg-slate-50 border-b border-slate-200">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                              <div>
                                <CardTitle className="text-slate-800">📊 Complete Performance Ranking</CardTitle>
                                <CardDescription>Retail locations ranked by net profit margin (corporate locations excluded)</CardDescription>
                              </div>
                              <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                                <div className="px-3 py-1.5 bg-blue-100 border border-blue-300 rounded-lg text-center">
                                  <span className="text-xs text-blue-600 font-medium block">Fiscal Year</span>
                                  <span className="text-sm font-bold text-blue-800">{selectedFiscalYear || 'All'}</span>
                                </div>
                                <div className="px-3 py-1.5 bg-emerald-100 border border-emerald-300 rounded-lg text-center">
                                  <span className="text-xs text-emerald-600 font-medium block">Time Frame</span>
                                  <span className="text-sm font-bold text-emerald-800">YTD (Year-to-Date)</span>
                                </div>
                                {currentPeriod && (
                                  <div className="px-3 py-1.5 bg-amber-100 border border-amber-300 rounded-lg text-center">
                                    <span className="text-xs text-amber-600 font-medium block">Through</span>
                                    <span className="text-sm font-bold text-amber-800">{formatPeriodDisplay(currentPeriod)}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                        </CardHeader>
                        <CardContent className="pt-6">
                          <div className="overflow-x-auto">
                            <table className="w-full">
                              <thead>
                                <tr className="border-b border-slate-200">
                                  <th className="text-left py-2 px-4 font-semibold text-slate-700">Rank</th>
                                  <th 
                                    className="text-left py-2 px-4 font-semibold text-slate-700 cursor-pointer hover:bg-slate-100 select-none"
                                    onClick={() => handleSort('location_name')}
                                  >
                                    Location {getSortIcon('location_name')}
                                  </th>
                                  <th 
                                    className="text-right py-2 px-4 font-semibold text-slate-700 cursor-pointer hover:bg-slate-100 select-none"
                                    onClick={() => handleSort('total_revenue')}
                                  >
                                    Revenue {getSortIcon('total_revenue')}
                                  </th>
                                  <th 
                                    className="text-right py-2 px-4 font-semibold text-slate-700 cursor-pointer hover:bg-slate-100 select-none"
                                    onClick={() => handleSort('total_expenses_combined')}
                                  >
                                    Total Expenses {getSortIcon('total_expenses_combined')}
                                  </th>
                                  <th 
                                    className="text-right py-2 px-4 font-semibold text-slate-700 cursor-pointer hover:bg-slate-100 select-none"
                                    onClick={() => handleSort('gross_profit')}
                                  >
                                    Gross Profit {getSortIcon('gross_profit')}
                                  </th>
                                  <th 
                                    className="text-right py-2 px-4 font-semibold text-slate-700 cursor-pointer hover:bg-slate-100 select-none"
                                    onClick={() => handleSort('gross_margin')}
                                  >
                                    Gross Margin % {getSortIcon('gross_margin')}
                                  </th>
                                  <th 
                                    className="text-right py-2 px-4 font-semibold text-slate-700 cursor-pointer hover:bg-slate-100 select-none"
                                    onClick={() => handleSort('net_ordinary_income')}
                                  >
                                    Net Ordinary Income {getSortIcon('net_ordinary_income')}
                                  </th>
                                  <th 
                                    className="text-right py-2 px-4 font-semibold text-slate-700 cursor-pointer hover:bg-slate-100 select-none"
                                    onClick={() => handleSort('total_profit_loss')}
                                    title="Net Profit is approximate within ±5% due to Corporate Overhead allocation across all locations"
                                  >
                                    Net Profit {getSortIcon('total_profit_loss')}
                                  </th>
                                  <th 
                                    className="text-right py-2 px-4 font-semibold text-slate-700 cursor-pointer hover:bg-slate-100 select-none"
                                    onClick={() => handleSort('net_margin')}
                                  >
                                    Net Margin % {getSortIcon('net_margin')}
                                  </th>
                                  <th className="text-center py-2 px-4 font-semibold text-slate-700">Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {combinedData.by_location
                                  .filter(loc => loc.location_name.toLowerCase() !== 'corp') // Exclude corp from analysis
                                  .map(loc => ({
                                    ...loc,
                                    total_expenses_combined: (loc.total_cogs || 0) + (loc.operating_expenses || 0),
                                    // Performance Ranking Table: Calculate Net Ordinary Income as Revenue - COGS - Expenses
                                    calculated_net_ordinary_income: (loc.total_revenue || 0) - (loc.total_cogs || 0) - (loc.operating_expenses || 0),
                                    // Calculate Net Profit as Net Ordinary Income - Corporate Overhead per location
                                    calculated_net_profit: (() => {
                                      const netOrdinary = (loc.total_revenue || 0) - (loc.total_cogs || 0) - (loc.operating_expenses || 0);
                                      // Sum all periods' per-location allocations for this location
                                      const corpOHPerLocation = corporateOverhead?.allocation_per_location || 0;
                                      return netOrdinary - corpOHPerLocation;
                                    })(),
                                    gross_margin: loc.total_revenue > 0 ? (loc.gross_profit / loc.total_revenue * 100) : 0,
                                    net_margin: (() => {
                                      const netOrdinary = (loc.total_revenue || 0) - (loc.total_cogs || 0) - (loc.operating_expenses || 0);
                                      const corpOHPerLocation = corporateOverhead?.allocation_per_location || 0;
                                      const calculatedNetProfit = netOrdinary - corpOHPerLocation;
                                      return loc.total_revenue > 0 ? (calculatedNetProfit / loc.total_revenue * 100) : 0;
                                    })()
                                  }))
                                  .sort((a, b) => {
                                    let aValue = a[sortField];
                                    let bValue = b[sortField];
                                    
                                    // Handle special field mappings
                                    if (sortField === 'net_ordinary_income') {
                                      aValue = a.calculated_net_ordinary_income;
                                      bValue = b.calculated_net_ordinary_income;
                                    } else if (sortField === 'total_profit_loss') {
                                      aValue = a.calculated_net_profit;
                                      bValue = b.calculated_net_profit;
                                    } else if (sortField === 'location_name') {
                                      aValue = aValue.toLowerCase();
                                      bValue = bValue.toLowerCase();
                                    }
                                    
                                    if (sortDirection === 'asc') {
                                      return aValue > bValue ? 1 : -1;
                                    } else {
                                      return aValue < bValue ? 1 : -1;
                                    }
                                  })
                                  .map((location, index) => {
                                    // Calculate rank based on net margin for consistent ranking display
                                    const sortedByNetMargin = combinedData.by_location
                                      .filter(loc => loc.location_name.toLowerCase() !== 'corp')
                                      .map(loc => ({...loc, net_margin: (loc.total_profit_loss / loc.total_revenue * 100)}))
                                      .sort((a, b) => b.net_margin - a.net_margin);
                                    const actualRank = sortedByNetMargin.findIndex(loc => loc.location_name === location.location_name) + 1;
                                    
                                    return (
                                    <tr key={location.location_name} className={`border-b border-slate-100 hover:bg-slate-50 ${
                                      actualRank === 1 ? 'bg-emerald-50' : 
                                      actualRank === sortedByNetMargin.length ? 'bg-red-50' : ''
                                    }`}>
                                      <td className="py-3 px-4">
                                        <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                                          actualRank === 1 ? 'bg-emerald-600 text-white' :
                                          actualRank === 2 ? 'bg-emerald-500 text-white' :
                                          actualRank === 3 ? 'bg-emerald-400 text-white' :
                                          actualRank >= sortedByNetMargin.length - 1 ? 'bg-red-500 text-white' :
                                          'bg-slate-200 text-slate-700'
                                        }`}>
                                          {actualRank}
                                        </span>
                                      </td>
                                      <td className={`py-3 px-4 font-medium ${location.status === 'closed' ? 'text-rose-600' : 'text-slate-800'}`}>
                                        {location.location_name}
                                        {location.status === 'closed' && (
                                          <span className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-rose-100 text-rose-600 align-middle">CLOSED</span>
                                        )}
                                      </td>
                                      <td className="py-3 px-4 text-right text-slate-800">{formatCurrency(location.total_revenue)}</td>
                                      <td className="py-3 px-4 text-right text-slate-800">{formatCurrency(location.total_expenses_combined)}</td>
                                      <td className={`py-3 px-4 text-right font-semibold ${location.gross_profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                        {formatCurrency(location.gross_profit)}
                                      </td>
                                      <td className={`py-3 px-4 text-right font-bold ${location.gross_margin >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                        {location.gross_margin.toFixed(1)}%
                                      </td>
                                      <td className={`py-3 px-4 text-right font-semibold ${location.calculated_net_ordinary_income >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                        {formatCurrency(location.calculated_net_ordinary_income)}
                                      </td>
                                      <td className={`py-3 px-4 text-right font-semibold ${location.calculated_net_profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                        {formatCurrency(location.calculated_net_profit)}
                                      </td>
                                      <td className={`py-3 px-4 text-right font-bold ${location.net_margin >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                        {location.net_margin.toFixed(1)}%
                                      </td>
                                      <td className="py-3 px-4 text-center">
                                        {actualRank === 1 ? '🏆' :
                                         actualRank <= 3 ? '⭐' :
                                         location.net_margin >= 0 ? '✅' :
                                         location.net_margin >= -5 ? '⚠️' : '🚨'}
                                      </td>
                                    </tr>
                                    )
                                  })}
                              </tbody>
                            </table>
                          </div>
                        </CardContent>
                        </Card>
                      )}
                    </>
                  ) : (
                    <Card className="border-slate-200">
                      <CardContent className="py-16 text-center">
                        <p className="text-slate-600">No performance data available</p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="comparison" data-testid="comparison-content">
                <ExpenseComparison 
                  user={user} 
                  adminViewMode={adminViewMode}
                  adminSelectedLocation={adminSelectedLocation}
                />
              </TabsContent>

              <TabsContent value="locations" data-testid="locations-content">
                <Card className="border-slate-200 shadow-sm">
                  <CardHeader className="bg-slate-50 border-b border-slate-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-slate-800">Performance by Location</CardTitle>
                        <CardDescription>Detailed breakdown of each location&apos;s financial performance</CardDescription>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant={locationBreakdownView === "mtd" ? "default" : "outline"}
                          size="sm"
                          onClick={() => setLocationBreakdownView("mtd")}
                        >
                          MTD {currentPeriod ? `(${currentPeriod})` : ''}
                        </Button>
                        <Button
                          variant={locationBreakdownView === "ytd" ? "default" : "outline"}
                          size="sm"
                          onClick={() => setLocationBreakdownView("ytd")}
                        >
                          YTD
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="space-y-4">
                      {locationBreakdownView === "ytd" ? (
                        // YTD View
                        combinedData.by_location
                        .sort((a, b) => a.location_name.localeCompare(b.location_name))
                        .map((location, index) => (
                        <div
                          key={index}
                          className="p-5 bg-slate-50 rounded-lg border border-slate-200 hover:shadow-md transition-shadow"
                          data-testid={`location-${index}`}
                        >
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex-1">
                              <h4 className={`font-semibold text-lg ${location.status === 'closed' ? 'text-rose-600' : 'text-slate-800'}`}>
                                {location.location_name}
                                {location.status === 'closed' && (
                                  <span className="ml-2 text-xs font-medium px-2 py-0.5 rounded bg-rose-100 text-rose-600">CLOSED</span>
                                )}
                              </h4>
                              <p className="text-xs text-slate-500 mt-1">
                                {location.record_count} records • {location.date_range.start} to {location.date_range.end}
                                {location.closed_date && ` • Closed ${location.closed_date}`}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                location.total_profit_loss >= 0
                                  ? 'bg-emerald-100 text-emerald-700'
                                  : 'bg-rose-100 text-rose-700'
                              }`}>
                                {location.total_profit_loss >= 0 ? 'Profitable' : 'Loss'}
                              </span>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => window.location.href = `/location/${encodeURIComponent(location.location_name)}?view=ytd&year=${selectedFiscalYear ? selectedFiscalYear.split('-').pop() : ''}`}
                                className="text-xs"
                              >
                                View Details
                              </Button>
                            </div>
                          </div>
                          <div className="grid grid-cols-4 gap-4">
                            <div>
                              <p className="text-xs text-slate-500 mb-1">Revenue</p>
                              <p className="font-semibold text-slate-800">{formatCurrency(location.total_revenue)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500 mb-1">Total Expenses</p>
                              <p className="font-semibold text-slate-800">{formatCurrency(location.total_expenses)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500 mb-1">Profit/Loss Before Corp OH</p>
                              <p className={`font-semibold ${
                                location.net_ordinary_income >= 0 ? 'text-emerald-600' : 'text-rose-600'
                              }`}>
                                {formatCurrency(location.net_ordinary_income)}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500 mb-1">Profit/Loss After Corp OH</p>
                              {(() => {
                                const corpOH = corporateOverhead?.allocation_per_location || 0;
                                const afterCorpOH = location.net_ordinary_income - corpOH;
                                return (
                                  <p className={`font-semibold ${
                                    afterCorpOH >= 0 ? 'text-emerald-600' : 'text-rose-600'
                                  }`}>
                                    {formatCurrency(afterCorpOH)}
                                  </p>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                      ))
                      ) : mtdLocationData ? (
                        // MTD View
                        Object.entries(mtdLocationData.summary || {})
                        .sort(([nameA], [nameB]) => nameA.localeCompare(nameB))
                        .map(([locationName, locationData], index) => {
                        const locStatus = combinedData?.by_location?.find(l => l.location_name === locationName);
                        const isClosed = locStatus?.status === 'closed';
                        return (
                        <div
                          key={index}
                          className="p-5 bg-slate-50 rounded-lg border border-slate-200 hover:shadow-md transition-shadow"
                        >
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex-1">
                              <h4 className={`font-semibold text-lg ${isClosed ? 'text-rose-600' : 'text-slate-800'}`}>
                                {locationName}
                                {isClosed && (
                                  <span className="ml-2 text-xs font-medium px-2 py-0.5 rounded bg-rose-100 text-rose-600">CLOSED</span>
                                )}
                              </h4>
                              <p className="text-xs text-slate-500 mt-1">
                                Period: {currentPeriod}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                locationData.net_income >= 0
                                  ? 'bg-emerald-100 text-emerald-700'
                                  : 'bg-rose-100 text-rose-700'
                              }`}>
                                {locationData.net_income >= 0 ? 'Profitable' : 'Loss'}
                              </span>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => window.location.href = `/location/${encodeURIComponent(locationName)}`}
                                className="text-xs"
                              >
                                View Details
                              </Button>
                            </div>
                          </div>
                          <div className="grid grid-cols-3 gap-4">
                            <div>
                              <p className="text-xs text-slate-500 mb-1">Revenue</p>
                              <p className="font-semibold text-slate-800">{formatCurrency(locationData.income || 0)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500 mb-1">Total Expenses</p>
                              <p className="font-semibold text-slate-800">{formatCurrency(locationData.total_expenses || 0)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500 mb-1">Net Income</p>
                              <p className={`font-semibold ${
                                locationData.net_income >= 0 ? 'text-emerald-600' : 'text-rose-600'
                              }`}>
                                {formatCurrency(locationData.net_income || 0)}
                              </p>
                            </div>
                          </div>
                        </div>
                      )})
                      ) : (
                        <div className="text-center py-8 text-slate-500">
                          Loading MTD data...
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {isSingleLocation && (
                <TabsContent value="mtd" data-testid="mtd-content">
                  <Card className="border-slate-200 shadow-sm">
                    <CardHeader className="bg-slate-50 border-b border-slate-200">
                      <CardTitle className="text-slate-800">
                        Month-to-Date Performance - {userLocation}
                      </CardTitle>
                      <CardDescription>
                        Detailed financial breakdown for {currentPeriod || 'current month'}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-6">
                      {mtdLocationData && mtdLocationData.summary && mtdLocationData.summary[userLocation] ? (
                        <div className="space-y-6">
                          {/* Summary Cards */}
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <Card className="border-slate-200 shadow-sm">
                              <CardHeader className="pb-3">
                                <CardDescription className="text-xs font-medium text-slate-500 uppercase">Revenue (MTD)</CardDescription>
                              </CardHeader>
                              <CardContent>
                                <div className="flex items-baseline gap-2">
                                  <DollarSign className="h-6 w-6 text-emerald-600" />
                                  <span className="text-3xl font-bold text-slate-800">
                                    {formatCurrency(mtdLocationData.summary[userLocation].income || 0)}
                                  </span>
                                </div>
                              </CardContent>
                            </Card>

                            <Card className="border-slate-200 shadow-sm">
                              <CardHeader className="pb-3">
                                <CardDescription className="text-xs font-medium text-slate-500 uppercase">COGS (MTD)</CardDescription>
                              </CardHeader>
                              <CardContent>
                                <div className="flex items-baseline gap-2">
                                  <Minus className="h-6 w-6 text-rose-600" />
                                  <span className="text-3xl font-bold text-slate-800">
                                    {formatCurrency(mtdLocationData.summary[userLocation].cogs || 0)}
                                  </span>
                                </div>
                              </CardContent>
                            </Card>

                            <Card className="border-slate-200 shadow-sm">
                              <CardHeader className="pb-3">
                                <CardDescription className="text-xs font-medium text-slate-500 uppercase">Expenses (MTD)</CardDescription>
                              </CardHeader>
                              <CardContent>
                                <div className="flex items-baseline gap-2">
                                  <DollarSign className="h-6 w-6 text-amber-600" />
                                  <span className="text-3xl font-bold text-slate-800">
                                    {formatCurrency(mtdLocationData.summary[userLocation].total_expenses || 0)}
                                  </span>
                                </div>
                              </CardContent>
                            </Card>

                            <Card className="border-slate-200 shadow-sm">
                              <CardHeader className="pb-3">
                                <CardDescription className="text-xs font-medium text-slate-500 uppercase">Net Income (MTD)</CardDescription>
                              </CardHeader>
                              <CardContent>
                                <div className="flex items-baseline gap-2">
                                  {mtdLocationData.summary[userLocation].net_income >= 0 ? (
                                    <TrendingUp className="h-6 w-6 text-emerald-600" />
                                  ) : (
                                    <TrendingDown className="h-6 w-6 text-rose-600" />
                                  )}
                                  <span className={`text-3xl font-bold ${
                                    mtdLocationData.summary[userLocation].net_income >= 0 
                                      ? 'text-emerald-600' 
                                      : 'text-rose-600'
                                  }`}>
                                    {formatCurrency(mtdLocationData.summary[userLocation].net_income || 0)}
                                  </span>
                                </div>
                              </CardContent>
                            </Card>
                          </div>

                          {/* Detailed Breakdown */}
                          <Card className="border-slate-200">
                            <CardHeader className="bg-slate-50 border-b border-slate-200">
                              <CardTitle className="text-lg">Financial Summary</CardTitle>
                            </CardHeader>
                            <CardContent className="pt-4">
                              <div className="space-y-3">
                                <div className="flex justify-between items-center py-2 border-b border-slate-100">
                                  <span className="text-slate-600">Revenue</span>
                                  <span className="font-semibold text-emerald-600">
                                    {formatCurrency(mtdLocationData.summary[userLocation].income || 0)}
                                  </span>
                                </div>
                                <div className="flex justify-between items-center py-2 border-b border-slate-100">
                                  <span className="text-slate-600">Cost of Goods Sold</span>
                                  <span className="font-semibold text-rose-600">
                                    ({formatCurrency(mtdLocationData.summary[userLocation].cogs || 0)})
                                  </span>
                                </div>
                                <div className="flex justify-between items-center py-2 border-b border-slate-200 font-semibold">
                                  <span className="text-slate-700">Gross Profit</span>
                                  <span className="text-slate-800">
                                    {formatCurrency(
                                      (mtdLocationData.summary[userLocation].income || 0) - 
                                      (mtdLocationData.summary[userLocation].cogs || 0)
                                    )}
                                  </span>
                                </div>
                                <div className="flex justify-between items-center py-2 border-b border-slate-100">
                                  <span className="text-slate-600">Operating Expenses</span>
                                  <span className="font-semibold text-amber-600">
                                    ({formatCurrency(mtdLocationData.summary[userLocation].total_expenses || 0)})
                                  </span>
                                </div>
                                <div className="flex justify-between items-center py-3 bg-slate-50 rounded-lg px-3 mt-2">
                                  <span className="text-slate-700 font-semibold text-lg">Net Income</span>
                                  <span className={`font-bold text-xl ${
                                    mtdLocationData.summary[userLocation].net_income >= 0 
                                      ? 'text-emerald-600' 
                                      : 'text-rose-600'
                                  }`}>
                                    {formatCurrency(mtdLocationData.summary[userLocation].net_income || 0)}
                                  </span>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        </div>
                      ) : (
                        <div className="text-center py-8 text-slate-500">
                          Loading month-to-date data...
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>
              )}

              <TabsContent value="ytd" data-testid="ytd-content">
                <Card className="border-slate-200 shadow-sm">
                  <CardHeader className="bg-slate-50 border-b border-slate-200">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-slate-800">Year-to-Date Financial Analysis</CardTitle>
                      <div className="text-sm font-medium text-slate-600 bg-blue-100 px-3 py-1 rounded">
                        {selectedFiscalYear || 'No Fiscal Year Selected'}
                      </div>
                    </div>
                    <CardDescription>
                      Cumulative financial performance for the selected fiscal year (Nov - Oct)
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-6">
                    {ytdLoading ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-slate-500" />
                        <span className="ml-2 text-slate-600">Loading YTD data...</span>
                      </div>
                    ) : ytdData ? (
                      <div className="space-y-6">
                        {/* YTD Summary Cards */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <Card className="p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <TrendingUp className="h-4 w-4 text-green-600" />
                              <span className="text-sm font-medium text-slate-600">YTD Income</span>
                            </div>
                            <p className="text-2xl font-bold text-slate-900">
                              {formatCurrency(ytdData.combined_totals.total_income)}
                            </p>
                          </Card>
                          
                          <Card className="p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <DollarSign className="h-4 w-4 text-blue-600" />
                              <span className="text-sm font-medium text-slate-600">YTD Gross Profit</span>
                            </div>
                            <p className="text-2xl font-bold text-slate-900">
                              {formatCurrency(ytdData.combined_totals.gross_profit)}
                            </p>
                            <p className="text-xs text-slate-500 mt-1">
                              Margin: {((ytdData.combined_totals.gross_profit / ytdData.combined_totals.total_income) * 100).toFixed(1)}%
                            </p>
                          </Card>
                          
                          <Card className="p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <AlertCircle className="h-4 w-4 text-red-600" />
                              <span className="text-sm font-medium text-slate-600">YTD Expenses</span>
                            </div>
                            <p className="text-2xl font-bold text-slate-900">
                              {formatCurrency(ytdData.combined_totals.total_expenses)}
                            </p>
                          </Card>
                          
                          <Card className="p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <FileSpreadsheet className="h-4 w-4 text-purple-600" />
                              <span className="text-sm font-medium text-slate-600">YTD Net Income</span>
                            </div>
                            <p className={`text-2xl font-bold ${ytdData.combined_totals.net_income >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {formatCurrency(ytdData.combined_totals.net_income)}
                            </p>
                            <p className="text-xs text-slate-500 mt-1">
                              Margin: {((ytdData.combined_totals.net_income / ytdData.combined_totals.total_income) * 100).toFixed(1)}%
                            </p>
                          </Card>
                        </div>

                        {/* YTD by Location */}
                        <div>
                          <h4 className="text-lg font-semibold text-slate-800 mb-4">
                            {isSingleLocation ? `YTD Performance - ${userLocation}` : 'YTD Performance by Location'}
                          </h4>
                          <div className="space-y-3">
                            {(isSingleLocation 
                              ? ytdData.by_location.filter(loc => loc.location_name === userLocation)
                              : ytdData.by_location
                            ).map((location, index) => (
                              <div
                                key={index}
                                className="p-4 bg-slate-50 rounded-lg border border-slate-200 hover:shadow-md transition-shadow"
                              >
                                <div className="flex items-start justify-between mb-3">
                                  <div className="flex-1">
                                    <h5 className="font-semibold text-slate-800">{location.location_name}</h5>
                                    <p className="text-xs text-slate-500 mt-1">
                                      {selectedFiscalYear} • {location.months_included?.length || 0} months included
                                    </p>
                                  </div>
                                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                    location.net_income >= 0
                                      ? 'bg-emerald-100 text-emerald-700'
                                      : 'bg-rose-100 text-rose-700'
                                  }`}>
                                    {location.net_income >= 0 ? 'Profitable' : 'Loss'}
                                  </span>
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                                  <div>
                                    <p className="text-slate-500 mb-1">YTD Income</p>
                                    <p className="font-semibold text-slate-800">{formatCurrency(location.total_income)}</p>
                                  </div>
                                  <div>
                                    <p className="text-slate-500 mb-1">YTD Gross Profit</p>
                                    <p className="font-semibold text-slate-800">{formatCurrency(location.gross_profit)}</p>
                                  </div>
                                  <div>
                                    <p className="text-slate-500 mb-1">YTD Expenses</p>
                                    <p className="font-semibold text-slate-800">{formatCurrency(location.total_expenses)}</p>
                                  </div>
                                  <div>
                                    <p className="text-slate-500 mb-1">YTD Net Income</p>
                                    <p className={`font-semibold ${location.net_income >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                      {formatCurrency(location.net_income)}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* YTD Comparison Chart */}
                        {!isSingleLocation && (
                          <div>
                            <h4 className="text-lg font-semibold text-slate-800 mb-4">YTD Financial Comparison</h4>
                            <ResponsiveContainer width="100%" height={400}>
                              <BarChart
                                data={ytdData.by_location.map(loc => ({
                                location: loc.location_name,
                                income: loc.total_income,
                                cogs: loc.total_cogs,
                                operating_expenses: loc.total_expenses,
                                net_income: loc.net_income
                              }))}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                              <XAxis dataKey="location" stroke="#64748b" style={{ fontSize: '12px' }} />
                              <YAxis stroke="#64748b" style={{ fontSize: '12px' }} />
                              <Tooltip
                                contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                                formatter={(value) => formatCurrency(value)}
                              />
                              <Legend wrapperStyle={{ paddingTop: '20px' }} />
                              <Bar dataKey="income" fill="#10b981" name="YTD Income" />
                              <Bar dataKey="cogs" fill="#f59e0b" name="Cost of Goods Sold" />
                              <Bar dataKey="operating_expenses" fill="#ef4444" name="Operating Expenses" />
                              <Bar dataKey="net_income" fill="#3b82f6" name="YTD Net Income" />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <FileSpreadsheet className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-slate-700 mb-2">No YTD Data Available</h3>
                        <p className="text-slate-500 mb-4">
                          {ytdYears.length === 0 
                            ? "Upload some monthly financial data to see Year-to-Date analysis."
                            : "No YTD data found for the selected year."
                          }
                        </p>
                        {user.role === 'admin' && (
                          <p className="text-xs text-slate-400">
                            YTD data is automatically generated when monthly data is uploaded or at month-end.
                          </p>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Fiscal Year Comparison Tab */}
              <TabsContent value="fiscal-comparison" data-testid="fiscal-comparison-content">
                {comparisonData && !comparisonLoading && fiscalYears.length >= 2 && comparisonData.monthly_comparisons && comparisonData.monthly_comparisons.length > 0 ? (
                  <Card className="border-blue-200 shadow-md bg-gradient-to-br from-blue-50 to-white">
                    <CardHeader className="border-b border-blue-100">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg font-bold text-slate-800 flex items-center gap-2">
                          <TrendingUp className="h-5 w-5 text-blue-600" />
                          Fiscal Year Comparison - Month by Month
                        </CardTitle>
                        <div className="text-sm text-slate-600">
                          <span className="font-semibold">{comparisonData.fiscal_year_1}</span>
                          <span className="mx-2">vs</span>
                          <span className="font-semibold text-blue-600">{comparisonData.fiscal_year_2}</span>
                          <span className="ml-2 text-xs text-slate-500">({comparisonData.total_months} months)</span>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-6">
                      {/* Totals Summary Card */}
                      {comparisonData.fy1_totals && comparisonData.fy2_totals && (
                        <div className="mb-6 bg-gradient-to-r from-emerald-50 to-blue-50 rounded-lg p-5 border-2 border-emerald-200">
                          <div className="flex items-center justify-between mb-4">
                            <h3 className="text-md font-bold text-slate-800 flex items-center gap-2">
                              <DollarSign className="h-5 w-5 text-emerald-600" />
                              Year-to-Date Totals
                            </h3>
                            <div className="text-xs text-slate-600 bg-white px-3 py-1 rounded-full">
                              Complete Fiscal Year Summary
                            </div>
                          </div>
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                            {/* Total Revenue */}
                            <div className="bg-white rounded-lg p-3 border border-emerald-100">
                              <div className="text-xs text-slate-500 uppercase font-medium mb-2">Total Revenue</div>
                              <div className="space-y-1">
                                <div className="text-xs text-slate-600">
                                  {comparisonData.fiscal_year_1}: <span className="font-semibold">{formatCurrency(comparisonData.fy1_totals.revenue)}</span>
                                </div>
                                <div className="text-sm text-blue-700 font-bold">
                                  {comparisonData.fiscal_year_2}: <span>{formatCurrency(comparisonData.fy2_totals.revenue)}</span>
                                </div>
                                <div className={`text-xs font-bold ${comparisonData.totals_variance.revenue.dollar >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                  {comparisonData.totals_variance.revenue.dollar >= 0 ? '+' : ''}{formatCurrency(comparisonData.totals_variance.revenue.dollar)}
                                  <span className="ml-1">({comparisonData.totals_variance.revenue.percent.toFixed(1)}%)</span>
                                </div>
                              </div>
                            </div>

                            {/* Total COGS */}
                            <div className="bg-white rounded-lg p-3 border border-emerald-100">
                              <div className="text-xs text-slate-500 uppercase font-medium mb-2">Total COGS</div>
                              <div className="space-y-1">
                                <div className="text-xs text-slate-600">
                                  {comparisonData.fiscal_year_1}: <span className="font-semibold">{formatCurrency(comparisonData.fy1_totals.cogs)}</span>
                                </div>
                                <div className="text-sm text-blue-700 font-bold">
                                  {comparisonData.fiscal_year_2}: <span>{formatCurrency(comparisonData.fy2_totals.cogs)}</span>
                                </div>
                                <div className={`text-xs font-bold ${comparisonData.totals_variance.cogs.dollar >= 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                                  {comparisonData.totals_variance.cogs.dollar >= 0 ? '+' : ''}{formatCurrency(comparisonData.totals_variance.cogs.dollar)}
                                  <span className="ml-1">({comparisonData.totals_variance.cogs.percent.toFixed(1)}%)</span>
                                </div>
                              </div>
                            </div>

                            {/* Total Gross Profit */}
                            <div className="bg-white rounded-lg p-3 border border-emerald-100">
                              <div className="text-xs text-slate-500 uppercase font-medium mb-2">Total Gross Profit</div>
                              <div className="space-y-1">
                                <div className="text-xs text-slate-600">
                                  {comparisonData.fiscal_year_1}: <span className="font-semibold">{formatCurrency(comparisonData.fy1_totals.gross_profit)}</span>
                                </div>
                                <div className="text-sm text-blue-700 font-bold">
                                  {comparisonData.fiscal_year_2}: <span>{formatCurrency(comparisonData.fy2_totals.gross_profit)}</span>
                                </div>
                                <div className={`text-xs font-bold ${comparisonData.totals_variance.gross_profit.dollar >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                  {comparisonData.totals_variance.gross_profit.dollar >= 0 ? '+' : ''}{formatCurrency(comparisonData.totals_variance.gross_profit.dollar)}
                                  <span className="ml-1">({comparisonData.totals_variance.gross_profit.percent.toFixed(1)}%)</span>
                                </div>
                              </div>
                            </div>

                            {/* Total Expenses */}
                            <div className="bg-white rounded-lg p-3 border border-emerald-100">
                              <div className="text-xs text-slate-500 uppercase font-medium mb-2">Total Expenses</div>
                              <div className="space-y-1">
                                <div className="text-xs text-slate-600">
                                  {comparisonData.fiscal_year_1}: <span className="font-semibold">{formatCurrency(comparisonData.fy1_totals.expenses)}</span>
                                </div>
                                <div className="text-sm text-blue-700 font-bold">
                                  {comparisonData.fiscal_year_2}: <span>{formatCurrency(comparisonData.fy2_totals.expenses)}</span>
                                </div>
                                <div className={`text-xs font-bold ${comparisonData.totals_variance.expenses.dollar >= 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                                  {comparisonData.totals_variance.expenses.dollar >= 0 ? '+' : ''}{formatCurrency(comparisonData.totals_variance.expenses.dollar)}
                                  <span className="ml-1">({comparisonData.totals_variance.expenses.percent.toFixed(1)}%)</span>
                                </div>
                              </div>
                            </div>

                            {/* Total Net Profit */}
                            <div className="bg-white rounded-lg p-3 border border-emerald-100">
                              <div className="text-xs text-slate-500 uppercase font-medium mb-2">Total Net Profit</div>
                              <div className="space-y-1">
                                <div className="text-xs text-slate-600">
                                  {comparisonData.fiscal_year_1}: <span className="font-semibold">{formatCurrency(comparisonData.fy1_totals.net_profit)}</span>
                                </div>
                                <div className="text-sm text-blue-700 font-bold">
                                  {comparisonData.fiscal_year_2}: <span>{formatCurrency(comparisonData.fy2_totals.net_profit)}</span>
                                </div>
                                <div className={`text-xs font-bold ${comparisonData.totals_variance.net_profit.dollar >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                  {comparisonData.totals_variance.net_profit.dollar >= 0 ? '+' : ''}{formatCurrency(comparisonData.totals_variance.net_profit.dollar)}
                                  <span className="ml-1">({comparisonData.totals_variance.net_profit.percent.toFixed(1)}%)</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Monthly Comparison Grid */}
                      <div className="space-y-3">
                        <h3 className="text-md font-bold text-slate-800 mb-3">Month-by-Month Comparison</h3>
                        {comparisonData.monthly_comparisons.map((monthData, index) => (
                          <div key={index} className="bg-white rounded-lg border-2 border-slate-200 p-4 hover:shadow-md transition-shadow duration-200">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="text-sm font-bold text-slate-700">{monthData.month}</h4>
                              <div className="flex items-center gap-2">
                                <span className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-600">
                                  {comparisonData.fiscal_year_1}
                                </span>
                                <span className="text-xs text-slate-400">vs</span>
                                <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700">
                                  {comparisonData.fiscal_year_2}
                                </span>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
                              {/* Revenue */}
                              <div className="bg-slate-50 rounded p-2">
                                <div className="text-xs text-slate-500 font-medium mb-1">Revenue</div>
                                <div className="text-xs text-slate-600">
                                  {comparisonData.fiscal_year_1}: <span className="font-semibold">{formatCurrency(monthData.fy1_data.revenue)}</span>
                                </div>
                                <div className="text-sm text-blue-700 font-bold">
                                  {comparisonData.fiscal_year_2}: <span>{formatCurrency(monthData.fy2_data.revenue)}</span>
                                </div>
                                <div className={`text-xs font-bold ${monthData.variance.revenue.dollar >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                  {monthData.variance.revenue.dollar >= 0 ? '+' : ''}{formatCurrency(monthData.variance.revenue.dollar)}
                                  <span className="ml-1">({monthData.variance.revenue.percent.toFixed(1)}%)</span>
                                </div>
                              </div>

                              {/* COGS */}
                              <div className="bg-slate-50 rounded p-2">
                                <div className="text-xs text-slate-500 font-medium mb-1">COGS</div>
                                <div className="text-xs text-slate-600">
                                  {comparisonData.fiscal_year_1}: <span className="font-semibold">{formatCurrency(monthData.fy1_data.cogs)}</span>
                                </div>
                                <div className="text-sm text-blue-700 font-bold">
                                  {comparisonData.fiscal_year_2}: <span>{formatCurrency(monthData.fy2_data.cogs)}</span>
                                </div>
                                <div className={`text-xs font-bold ${monthData.variance.cogs.dollar >= 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                                  {monthData.variance.cogs.dollar >= 0 ? '+' : ''}{formatCurrency(monthData.variance.cogs.dollar)}
                                  <span className="ml-1">({monthData.variance.cogs.percent.toFixed(1)}%)</span>
                                </div>
                              </div>

                              {/* Gross Profit */}
                              <div className="bg-slate-50 rounded p-2">
                                <div className="text-xs text-slate-500 font-medium mb-1">Gross Profit</div>
                                <div className="text-xs text-slate-600">
                                  {comparisonData.fiscal_year_1}: <span className="font-semibold">{formatCurrency(monthData.fy1_data.gross_profit)}</span>
                                </div>
                                <div className="text-sm text-blue-700 font-bold">
                                  {comparisonData.fiscal_year_2}: <span>{formatCurrency(monthData.fy2_data.gross_profit)}</span>
                                </div>
                                <div className={`text-xs font-bold ${monthData.variance.gross_profit.dollar >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                  {monthData.variance.gross_profit.dollar >= 0 ? '+' : ''}{formatCurrency(monthData.variance.gross_profit.dollar)}
                                  <span className="ml-1">({monthData.variance.gross_profit.percent.toFixed(1)}%)</span>
                                </div>
                              </div>

                              {/* Expenses */}
                              <div className="bg-slate-50 rounded p-2">
                                <div className="text-xs text-slate-500 font-medium mb-1">Expenses</div>
                                <div className="text-xs text-slate-600">
                                  {comparisonData.fiscal_year_1}: <span className="font-semibold">{formatCurrency(monthData.fy1_data.expenses)}</span>
                                </div>
                                <div className="text-sm text-blue-700 font-bold">
                                  {comparisonData.fiscal_year_2}: <span>{formatCurrency(monthData.fy2_data.expenses)}</span>
                                </div>
                                <div className={`text-xs font-bold ${monthData.variance.expenses.dollar >= 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                                  {monthData.variance.expenses.dollar >= 0 ? '+' : ''}{formatCurrency(monthData.variance.expenses.dollar)}
                                  <span className="ml-1">({monthData.variance.expenses.percent.toFixed(1)}%)</span>
                                </div>
                              </div>

                              {/* Net Profit */}
                              <div className="bg-slate-50 rounded p-2">
                                <div className="text-xs text-slate-500 font-medium mb-1">Net Profit</div>
                                <div className="text-xs text-slate-600">
                                  {comparisonData.fiscal_year_1}: <span className="font-semibold">{formatCurrency(monthData.fy1_data.net_profit)}</span>
                                </div>
                                <div className="text-sm text-blue-700 font-bold">
                                  {comparisonData.fiscal_year_2}: <span>{formatCurrency(monthData.fy2_data.net_profit)}</span>
                                </div>
                                <div className={`text-xs font-bold ${monthData.variance.net_profit.dollar >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                  {monthData.variance.net_profit.dollar >= 0 ? '+' : ''}{formatCurrency(monthData.variance.net_profit.dollar)}
                                  <span className="ml-1">({monthData.variance.net_profit.percent.toFixed(1)}%)</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="flex items-center justify-center py-12">
                    <AlertCircle className="h-6 w-6 text-slate-400" />
                    <span className="text-sm text-slate-500 ml-2">No fiscal year comparison data available</span>
                  </div>
                )}
              </TabsContent>

            </Tabs>
          </>
        )}
      </div>

      {/* MFA Setup Modal/Page */}
      {showMfaSetup && (
        <div className="fixed inset-0 bg-white z-50 overflow-y-auto">
          {mfaEnforced && (
            <div className="bg-red-500 text-white px-4 py-3 text-center">
              <strong>⚠️ Security Requirement:</strong> You must enable Multi-Factor Authentication (MFA) to continue using this application.
            </div>
          )}
          <MFASetup 
            user={user} 
            onBack={() => {
              // Only allow closing if MFA is not being enforced
              if (!mfaEnforced) {
                setShowMfaSetup(false);
              }
            }}
            onMfaEnabled={() => {
              // When MFA is enabled, allow closing and clear enforcement
              setMfaEnforced(false);
              setShowMfaSetup(false);
            }}
            enforced={mfaEnforced}
            onLogout={onLogout}
          />
        </div>
      )}
    </div>
  );
};

export default Dashboard;
